import { Button, ButtonGroup, Col, Dropdown, Nav, NavItem, Row, Tab } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "redux/store";
import { formatToAUD } from "helpers/numbersFormater";
import StatisticsWidget from "./StatisticsWidget";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { eChartColours, getMainColors, getRandomColours, rainbow } from "constants/colors";
import { Dashboard } from "pages/support/dashboard/Dashboard";
import { useDispatch } from "react-redux";
import { updateDrillDown } from "redux/slices/Dashboard";
import { DataZoomComponentOption, EChartsOption } from "echarts";
import EChartsReact from "echarts-for-react";
import { useSearchParams } from "react-router-dom";

type StatisticGraphType = "Sales" | "Scripts" | "Profit" | "Basket" | "NegativeSOH" | "StocksCreated" | "NegativeGP$Sales" | "PendingPriceChanges";

export const StatisticsGraph = (data: {
    pharmacy: string,
    graphData: {
        name: string,
        data: (number | null)[],
        totals: number,
        group: string,
    }[]
}[], graphTitle: string, graphType: StatisticGraphType) => {
    const colours = getRandomColours(data.length);
    const currentYear = new Date().getFullYear();
    const years = [currentYear - 3, currentYear - 2, currentYear - 1, currentYear];
    const [selectedOption, setSelectedOption] = useState<string>("");
    const chartRef = useRef<Chart>(null);
    const drilldownChartRef = useRef(null);
    const dashboard = useAppSelector((state: RootState) => state.dashboard);

    const mappedData = useMemo(() => {
        if (selectedOption === "Combined") {
            //If they selected combined, then we'll need to combine all group data under the same name
            const flat = data.flatMap((e) => e.graphData.map((z) => ({ name: z.name, data: z.data, group: z.group })));

            let combined: {
                name: string;
                data: (number | null)[];
                group: string;
            }[] = [];

            flat.forEach((e) => {
                const match = combined.find((x) => x.name === e.name);

                if (match) {
                    let newData: (number | null)[] = [];
                    for (let i = 0; i < e.data.length; i++) {
                        if (e.data[i] !== null && match.data[i] !== null) {
                            newData[i] = (match.data[i] ?? 0) + (e.data[i] ?? 0);
                        } else {
                            newData[i] = null;
                        }
                    }
                    match.data = newData;
                } else {
                    combined.push({ ...e, group: "Combined" });
                }
            })
            return combined;
        } else {
            return data.flatMap((e) => e.graphData.filter(x => x.group === selectedOption).map((z) => ({ name: z.name, data: z.data, group: z.group })))
        }
    }, [selectedOption, data]);


    const chartOpts = useMemo<EChartsOption>(() => {
        // console.log("Mapped data: ", mappedData);
        // console.log("Data: ", data);

        const categories = years.flatMap((year) =>
            ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((month) => `${month} ${year}`)
        );

        let zoom: DataZoomComponentOption = {};

        let selectedYear = Number(dashboard.period);

        if (!Number.isNaN(selectedYear)) {
            const yearIndex = years.indexOf(selectedYear);
            zoom.start = (yearIndex - 1) * 25
            zoom.end = (yearIndex + 1) * 25
        }

        return {
            color: eChartColours,
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross',
                    snap: true,
                },
                valueFormatter(value, dataIndex) {
                    if (value == null) return "N/A";
                    const valueNumber = value as Number;
                    return ["Sales", "Profit"].includes(graphType) && value ? valueNumber.formatAsPrice(false, true, true) : valueNumber.formatAsPrice(false, false, !["Scripts"].includes(graphType));
                }
            },
            dataZoom: [
                {
                    type: 'slider',
                    show: true,
                    xAxisIndex: [0],
                    showDataShadow: false,
                    ...zoom, 
                    bottom: 52,
                    labelPrecision: 10
                },
            ],
            series: mappedData.map(d => {
                return {
                    type: 'line',
                    name: d.name,
                    data: d.data,
                }
            }),
            toolbox: {
                feature: {
                    saveAsImage: {
                        title: 'Save',
                        name: graphTitle + " - " + selectedOption + "_" + new Date().toLocaleDateString(),
                        show: true,
                    }
                }
            },
            legend: {
                data: mappedData.map(d => d.name),
                textStyle: {
                    color: '#6c757d',
                },
                bottom: '0%',
                type: 'scroll',
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: 40,
                containLabel: true
            },
            xAxis: [
                {
                    splitLine: {
                        show: true,
                        interval: (index, value) => {
                            return index%12 === 0;
                        },
                    },
                    type: 'category',
                    boundaryGap: false,
                    data: categories,
                    axisLabel: {
                        color: '#6c757d',
                        formatter: (value: string) => value, //.split(' ')[0], // Only display the month
                    },
                    axisLine: {
                        lineStyle: {
                            color: '#6c757d',
                        },
                    },
                    axisPointer: {
                        show: true,
                        snap: true,
                        type: 'shadow',
                        label: {
                            show: false,
                        }
                    },
                },
                {
                    // This second x-axis will group the months by year in groups of 12 and display the year
                    type: 'category',
                    boundaryGap: false,
                    // The data should be structured like ["2021", "2021", ..., "2022", "2022", ...] where each year is repeated 12 times
                    data: Array(years.length * 12).fill(''),
                    // I want the year to be displayed underneath the months
                    axisLabel: {
                        interval: 11,
                        color: '#6c757d',
                    },
                    axisLine: {
                        lineStyle: {
                            color: '#6c757d',
                        },
                        show: false,
                    },
                    axisPointer: {
                        show: false,
                    },
                    splitLine: {
                        interval: (ix, _) => {
                            return ix !== 0 && ix % 12 === 0;
                        },
                        show: false,
                    },
                    axisTick: {
                        show: true,
                        interval: (ix, _) => {
                            return ix !== 0 && ix % 12 === 0;
                        },
                        alignWithLabel: true,
                        length: 16,

                    },
                    position: 'bottom',
                    offset: 40,
                },
                {
                    // Sometimes you gotta fake it till you make it, so this is the years values shown with a gap between to fake the spacing
                    type: 'category',
                    boundaryGap: false,
                    data: ['', ...years.map(i => [i, ' ']).flat()],
                    axisLabel: {
                        interval: 0,
                        color: '#6c757d',
                    },
                    show: false,
                axisPointer: {
                    show: false,
                },
                splitLine: {
                    interval: 11,
                    show: false,
                },
                axisTick: {
                    show: true,
                    interval: 11,
                    alignWithLabel: true,
                    length: 16

                },
                position: 'bottom',
                offset: 40,
            },
            {
                // Sometimes you gotta fake it till you make it, so this is the years values shown with a gap between to fake the spacing
                type: 'category',
                boundaryGap: false,
                data: ['', ...years.map(i => [i , ' ']).flat()],
                axisLabel: {
                    interval: 0,
                    color: '#6c757d',
                },
                axisLine: {
                    show: false,
                },
                axisPointer: {
                    show: false,
                },
                splitLine: {
                    show: false,
                },
                axisTick: {
                    show: false,
                },
                position: 'bottom',
                offset: 35,
            }],
            yAxis: {
                type: 'value',
                axisLabel: {
                    color: '#6c757d',
                    formatter: (value: number) => ["Sales", "Profit"].includes(graphType) ? value.formatAsPrice(false, true, true) : value.formatAsPrice(false, false, !["Scripts"].includes(graphType)),
                },
                axisPointer: {
                    show: false
                },
                axisLine: {
                    lineStyle: {
                        color: '#6c757d',
                    },
                },
                splitLine: {
                    lineStyle: {
                        color: '#e9ecef',
                    },
                },
            },
        };
    }, [mappedData]);

    const options = useMemo(() => {
        let opts = data.flatMap((e) => e.graphData.map((z) => z.group)).removeDuplicates((e) => e);
        if (opts.length > 1)
            opts = ["Combined", ...opts];
        return opts;
    }, [data]);

    useEffect(() => {
        console.log("Selecting first option");
        console.log(options);
        setSelectedOption(options[0]);
    }, [options]);

    return (
        <>
            <div className="d-flex flex-column h-100 w-100">
                <div className="d-flex w-100 justify-content-center text-center h-fit">
                    <div className="hide-when-small" style={{ minWidth: 430 }}>
                        <Tab.Container defaultActiveKey={selectedOption} >
                            <Nav variant="pills" justify className="bg-nav-pills">
                                {options.map((option) => {
                                    return <Nav.Item key={option} onClick={() => setSelectedOption(option)}>
                                        <Nav.Link eventKey={option} >
                                            <span className="d-md-block">
                                                {option}
                                            </span>
                                        </Nav.Link>
                                    </Nav.Item>;
                                })}
                            </Nav>
                        </Tab.Container>
                    </div>
                    <Dropdown className="d-sm-none ms-auto" style={{ paddingLeft: "6px" }}>
                        <Dropdown.Toggle id="dropdown-basic2" variant="primary" style={{ borderRadius: "5px", fontSize: `var(--bs-nav-link-font-size)` }}>
                            {selectedOption}
                        </Dropdown.Toggle>
                        <Dropdown.Menu style={{ width: "80px" }}>
                            {options.map((tab, index) => (
                                <Dropdown.Item
                                    key={tab}
                                    active={selectedOption === tab}
                                    onClick={() => setSelectedOption(tab)}
                                >{tab}</Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                <EChartsReact
                    ref={drilldownChartRef}
                    option={chartOpts}
                    style={{ height: "95%" }}
                />
            </div>
        </>
    )
}

const Statistics = () => {
    const dashboard = useAppSelector((state: RootState) => state.dashboard);
    const dispatch = useDispatch();
    const showDrillDown = useCallback(() => {
        return !['Month', 'Week', 'Day'].includes(dashboard.period);
    }, [dashboard.period]);

    return (
        <>
            <Row className="mb-2 row-gap-2">
                <Col lg={6} xl={6} className="pe-sm-2 pe-md-2 pe-lg-0">
                    <StatisticsWidget
                        icon="dripicons-cart"
                        title="Total Sales"
                        drilldownKeyword="Sales"
                        isMoney={true}
                        stats={dashboard.sales.total}
                        trend={dashboard.sales.prevTotal ? {
                            value: ((dashboard.sales.total - dashboard.sales.prevTotal) / dashboard.sales.prevTotal * 100),
                            time: dashboard.sales.message,
                        } : undefined}
                        rightPanel={[
                            { name: "OTC", value: formatToAUD(dashboard.sales.otc) },
                            { name: "Scripts", value: formatToAUD(dashboard.sales.script) },
                        ]}
                        separatorColour={"rgba(109, 117, 125, 0.2)"}
                        drillDownClicked={() => {
                            dispatch(updateDrillDown({
                                drillDownGraphType: "Sales"
                            }));
                            return true;
                        }}
                        enableDrillDown={showDrillDown()}
                    >{StatisticsGraph(dashboard.drillDownGraph.data, "Total Sales", "Sales")}</StatisticsWidget>
                </Col>
                <Col lg={6} xl={6} className="pe-sm-2 pe-md-2 pe-lg-0">
                    <StatisticsWidget
                        icon="mdi mdi-file-document-multiple-outline"
                        title="Total Scripts"
                        drilldownKeyword="Scripts"
                        stats={dashboard.scripts.total}
                        trend={dashboard.scripts.prevTotal ? {
                            value: ((dashboard.scripts.total - dashboard.scripts.prevTotal) / dashboard.scripts.prevTotal * 100),
                            time: dashboard.scripts.message,
                        } : undefined}
                        rightPanel={[
                            { name: "Paper", value: Number(dashboard.scripts.paper).toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                            { name: "Paperless", value: Number(dashboard.scripts.paperless).toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                            { name: "Owing", value: Number(dashboard.scripts.owing).toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                        ]}
                        separatorColour={"rgba(109, 117, 125, 0.2)"}
                        drillDownClicked={() => {
                            dispatch(updateDrillDown({
                                drillDownGraphType: "Scripts"
                            }));
                            return true;
                        }}
                        enableDrillDown={showDrillDown()}
                    >{StatisticsGraph(dashboard.drillDownGraph.data, "Total Scripts", "Scripts")}</StatisticsWidget>
                </Col>
                {/* <Col lg={4} xl={4} className="pe-sm-2 pe-md-2 pe-lg-0">
                    <StatisticsWidget
                        icon="mdi mdi-currency-usd"
                        title="Preferred Brands"
                        stats={dashboard.preferredBrand.total ? Math.round(dashboard.preferredBrand.count / dashboard.preferredBrand.total * 100) : 0}
                        isPercent={true}
                        rightPanel={[
                            { name: "Scripts", value: dashboard.preferredBrand.message[0] ? dashboard.preferredBrand.message[0] : "0/0 scripts" },
                            { name: "Lost Profit", value: dashboard.preferredBrand.message[1] ? formatToAUD(dashboard.preferredBrand.message[1]) : "0.00" },
                        ]}
                        separatorColour={"rgba(109, 117, 125, 0.2)"}
                    />
                </Col> */}
            </Row>
            <Row className="mb-0 row-gap-2">
                <Col lg={6} xl={6} className="pe-sm-2 pe-md-2 pe-lg-0">
                    <StatisticsWidget
                        icon="mdi mdi-currency-usd"
                        title="Total Gross Profit"
                        drilldownKeyword="Profit"
                        isMoney={true}
                        stats={Number(dashboard.grossProfit.total.toFixed(2))}
                        trend={dashboard.grossProfit.prevTotal ? {
                            value: ((dashboard.grossProfit.total - dashboard.grossProfit.prevTotal) / dashboard.grossProfit.prevTotal * 100),
                            time: dashboard.grossProfit.message,
                        } : undefined}
                        rightPanel={[
                            { name: "OTC", value: formatToAUD(dashboard.grossProfit.otc) },
                            { name: "Scripts", value: formatToAUD(dashboard.grossProfit.script) },
                        ]}
                        separatorColour={"rgba(109, 117, 125, 0.2)"}
                        drillDownClicked={() => {
                            dispatch(updateDrillDown({
                                drillDownGraphType: "Profit"
                            }));
                            return true;
                        }}
                        enableDrillDown={showDrillDown()}
                    >{StatisticsGraph(dashboard.drillDownGraph.data, "Total Gross Profit", "Profit")}</StatisticsWidget>
                </Col>
                <Col lg={6} xl={6} className="pe-sm-2 pe-md-2 pe-lg-0">
                    <StatisticsWidget
                        icon="uil-shopping-basket"
                        title="Average Basket Size"
                        drilldownKeyword="Basket"
                        stats={Number(dashboard.basketStats.total.toFixed(2))}
                        trend={dashboard.basketStats.prevTotal ? {
                            value: ((dashboard.basketStats.total - dashboard.basketStats.prevTotal) / dashboard.basketStats.prevTotal * 100),
                            time: dashboard.basketStats.message,
                        } : undefined}
                        extraValue={dashboard.basketStats.totalDollar.toFixed(2)}
                        rightPanel={[
                            { name: "OTC", value: dashboard.basketStats.count > 0 ? ((dashboard.basketStats.otc / dashboard.basketStats.count).toFixed(2) + " (" + formatToAUD((dashboard.basketStats.otcDollar / dashboard.basketStats.count).toFixed(2)) + ")") : "0 ($0)" },
                            { name: "Scripts", value: dashboard.basketStats.count > 0 ? ((dashboard.basketStats.scripts / dashboard.basketStats.count).toFixed(2) + " (" + formatToAUD((dashboard.basketStats.scriptsDollar / dashboard.basketStats.count).toFixed(2)) + ")") : "0 ($0)" },
                            { name: "Refunds", value: dashboard.basketStats.refunds.toString() + " (" + formatToAUD(dashboard.basketStats.refundsDollar) + ")" },
                        ]}
                        separatorColour={"rgba(109, 117, 125, 0.2)"}
                        drillDownClicked={() => {
                            dispatch(updateDrillDown({
                                drillDownGraphType: "Basket"
                            }));
                            return true;
                        }}
                        enableDrillDown={showDrillDown()}
                    >{StatisticsGraph(dashboard.drillDownGraph.data, "Average Basket Size", "Basket")}</StatisticsWidget>
                </Col>
                {/* <Col lg={4} xl={4} className="pe-sm-2 pe-md-2 pe-lg-0">
                    <StatisticsWidget
                        icon="uil-graph-bar"
                        title="Quick Stats"
                        leftPanel={[
                            { name: "Stock Value", value: formatToAUD(dashboard.quickStatAmounts.stockValue)},
                        ]}
                        rightPanel={[
                            { name: "Negative SOH", value: dashboard.quickStatAmounts.negativeStockCount.formatAsPrice(false, false, false) + ' lines' },
                        ]}
                        separatorColour={"rgba(109, 117, 125, 0.2)"}
                    />
                </Col> */}
            </Row>
        </>
    );
};

export default Statistics;
